import ReactECharts from "echarts-for-react";
import { useEffect, useRef, useState } from "react";
import styles from "styles/pages/reports/reports.module.scss";
import ComboFilters from "../../ComboFilters";
import {
  getGrowthPercentage,
  getLastSixYears,
  months,
  Trend,
} from "../../Reports";
import { If } from "shared/components/control-statements";
import dayjs from "dayjs";
import { Loading } from "@components";

export const EngagementGroupChart = ({
  selectedData,
  onFetchData,
  onClose,
  loading,
  startOn2025,
  isCashback
}) => {
  const getCurrentMonthYear = () => {
    const today = new Date();

    const date = new Date(today.getFullYear(), today.getMonth());
    const mes = months[date.getMonth()];
    const ano = date.getFullYear();

    return {
      name: `${mes} ${ano}`,
      id: `${date.getMonth() + 1}`,
    };
  };

  const echartsRef = useRef(null);
  const currentYear = new Date().getFullYear();
  const currentMonth = getCurrentMonthYear();

  const [data, setData] = useState({
    never_received: [],
    never_used: [],
    used_more_than_a_time: [],
    used_more_than_ten_times: [],
    used_more_than_three_times: [],
  });

  const [selectedFilter, setSelectedFilter] = useState("year");
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState({
    name: currentYear,
    id: currentYear,
  });

  const daysInMonth = Array.from(
    {
      length: dayjs(
        `${selectedMonth.year}-${String(selectedMonth.id).padStart(2, "0")}`
      )
        .endOf("month")
        .date(),
    },
    (_, i) => i + 1
  );

  const formatData = () => {
    const today = dayjs();

    const result = {};

    if (selectedYear.id < today.year()) return data;

    const isYearFilter = selectedFilter === "year";
    const isCurrentYear = selectedYear.id === today.year();

    const isMonthFilter = selectedFilter === "month";
    const isCurrentMonth =
      selectedYear?.id === today.year() &&
      Number(selectedMonth?.id) === today.month() + 1;

    let limit = null;

    for (const key in data) {
      result[key] = data[key].map((value) => (value === 0 ? null : value));
    }

    if (isYearFilter && isCurrentYear) limit = today.month();

    if (isMonthFilter)
      if (isCurrentMonth) limit = today.date() - 2;
      else return result;

    for (const key in result) {
      result[key] =
        limit !== null ? result[key].slice(0, limit + 1) : result[key];
    }

    return result;
  };

  const series = formatData();

  const roundValue = (v) => {
    return Math.round(v / 10) * 10;
  };

  const getVisibleSeriesMin = (selected) => {
    let min = Infinity;
    let max = -Infinity;

    const labels = {
      "Nunca ganhou cashback": "never_received",
      "Ganhou cashback e nunca usou": "never_used",
      "Usou ao menos 1 vez": "used_more_than_a_time",
      "Usou ao menos 3 vezes": "used_more_than_three_times",
      "Usou ao menos 10 vezes": "used_more_than_ten_times",
    };

    for (const label in selected) {
      if (!selected[label]) continue;

      const key = labels[label];
      const serieData = data[key];

      if (!serieData) continue;

      const values = serieData
        .map((d) => Number(d))
        .filter((v) => !isNaN(v) && v !== 0);

      if (values.length === 0) continue;

      const localMin = Math.min(...values);
      const localMax = Math.max(...values);
      min = Math.min(min, localMin);
      max = Math.max(max, localMax);
    }

    if (!isFinite(min)) return 0;

    const padding = (max - min) * 0.2;
    const ajustado = Math.max(0, Math.floor(min - padding));
    const rounded = roundValue(ajustado);

    return rounded;
  };

  const handleLegendSelectChanged = (e) => {
    const instance = echartsRef.current.getEchartsInstance();
    const selected = e.selected;

    const newMin = getVisibleSeriesMin(selected);

    instance.setOption({
      yAxis: {
        startValue: newMin,
      },
    });
  };

  const legends = [
    {
      name: isCashback ? 'Nunca ganhou cashback': 'Nunca ganhou pontos',
      color: "#FFFFFF",
      data: series.never_received,
      show: (series.never_received || []).some((value) => value),
    },
    {
      name: isCashback ? 'Ganhou cashback e nunca usou': 'Ganhou pontos e nunca usou',
      color: "#F1B6C6",
      data: series.never_used,
      show: (series.never_used || []).some((value) => value),
    },
    {
      name: isCashback ? 'Usou ao menos 1 vez': 'Usou pontos ao menos 1 vez',
      color: "#55E092",
      data: series.used_more_than_a_time,
      show: (series.used_more_than_a_time || []).some((value) => value),
    },
    {
      name: isCashback ? 'Usou ao menos 3 vezes': 'Usou pontos ao menos 3 vezes',
      color: "#FABF55",
      data: series.used_more_than_three_times,
      show: (series.used_more_than_three_times || []).some((value) => value),
    },
    {
      name: isCashback ? 'Usou ao menos 10 vezes': 'Usou pontos ao menos 10 vezes',
      color: "#05AFF2",
      data: series.used_more_than_ten_times,
      show: (series.used_more_than_ten_times || []).some((value) => value),
    },
  ];

  const buildOptions = () => {
    return {
      grid: {
        left: 10,
        right: 30,
        top: 30,
        bottom: 70,
        containLabel: true,
      },
      tooltip: {
        trigger: "item",
        axisPointer: {
          type: "none",
        },
        backgroundColor: "#2B2B2B",
        borderRadius: 8,
        padding: [8, 8],
        borderWidth: 0,
        textStyle: {
          color: "#FFF",
          fontSize: 10,
        },
        formatter: (params) => {
          const name = params.name;

          if (params.data === null || params.value == null) return;

          const formattedValue = params.value.toLocaleString("pt-BR");

          return `
            <div>
              <strong>${name}${
            selectedFilter === "month" ? ` de ${selectedMonth.name}` : ""
          }</strong><br/><br/>
              ${params.marker} ${params.seriesName}: <b>${formattedValue}</b>
            </div>
          `;
        },
      },
      legend: {
        data: legends.filter((item) => item.show).map((a) => a.name),
        type: "scroll",
        orient: "horizontal",
        pageIconColor: "#6D6DF2",
        pageIconInactiveColor: "#4C4C4C",
        backgroundColor: "#1C1C1C",
        borderRadius: 10,
        padding: 20,
        pageIconSize: 10,
        pageTextStyle: {
          color: "#B3B3B3",
          fontSize: 10,
          fontWeight: 800,
        },
        pageIcons: {
          horizontal: [
            "path://M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6z",
            "path://M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z",
          ],
        },
        top: "bottom",
        icon: "circle",
        itemWidth: 8,
        itemHeight: 8,
        itemGap: 10,
        textStyle: {
          color: "#808080",
          fontSize: 12,
        },
        inactiveColor: "#555",
      },
      xAxis: {
        type: "category",
        data: selectedFilter === "year" ? months : Array.from(daysInMonth),
        axisLine: {
          show: true,
          lineStyle: {
            color: "rgba(217, 217, 217, 0.5)",
          },
        },
        splitLine: {
          lineStyle: {
            color: "rgba(51, 51, 51, 0.55)",
            width: 1,
            type: "solid",
          },
        },
      },
      yAxis: {
        type: "value",
        axisLine: {
          show: true,
          lineStyle: {
            color: "rgba(217, 217, 217, 0.5)",
          },
        },
        axisLabel: {
          formatter: (value) => `${value.toLocaleString("pt-BR")}`,
        },
        splitLine: {
          lineStyle: {
            color: "rgba(51, 51, 51, 0.55)",
            width: 0,
            type: "solid",
          },
        },
      },
      series: legends.map((legend) => ({
        name: legend.name,
        type: "line",
        data: legend.data || [],
        color: legend.color,
        symbolSize: 8,
        itemStyle: {
          color: legend.color,
          borderColor: legend.color,
          borderWidth: 2,
        },
        lineStyle: {
          width: 2,
          color: legend.color,
        },
        symbol: "circle",
        connectNulls: false,
      })),
    };
  };

  const [options, setOptions] = useState(buildOptions());

  useEffect(() => {
    const chart = echartsRef.current?.getEchartsInstance();
    if (chart) {
      setTimeout(() => chart.resize(), 0);
    }
  }, []);

  const fetchData = async () => {
    if (!onFetchData) {
      return;
    }

    setData([]);

    const payload = { year: selectedYear?.id };
    payload.month = selectedFilter === "month" ? selectedMonth?.id : null;

    const results = await onFetchData(payload);

    const series = {
      never_received: [],
      never_used: [],
      used_more_than_a_time: [],
      used_more_than_three_times: [],
      used_more_than_ten_times: [],
    };

    results.forEach((result) => {
      for (const key in series) {
        if (result && result[key] !== undefined && result[key] !== null) {
          series[key].push(result[key]);
        } else {
          series[key].push(0);
        }
      }
    });

    console.log("series", series);

    setData(series);
  };

  useEffect(() => {
    fetchData();
  }, [selectedFilter, selectedYear, selectedMonth]);

  useEffect(() => {
    setOptions(buildOptions());
  }, [data]);

  const TrendElement = ({ data }) => {
    return (
      <div className={styles.legend}>
        <div className="d-flex align-center">
          <div className={styles.dot} style={{ background: data.color }} />
          <p className={styles.name}>{data.name}</p>
        </div>
        {data.show && (
          <Trend
            growth={getGrowthPercentage({
              data: data.data,
              selectedFilter,
              selectedMonth,
              selectedYear,
            })}
          />
        )}
      </div>
    );
  };

  return (
    <div className={styles.monthYearLegendChartContainer}>
      <div className={styles.monthYearHeader}>
        <p className={styles.title}>{selectedData?.name}</p>
        <div className="d-flex align-center">
          <div className={styles.close} onClick={onClose}>
            <i className="fas fa-times" />
          </div>
        </div>
      </div>

      <If cond={loading}>
        <div className={styles.loading}>
          <Loading />
        </div>
      </If>

      <If cond={!loading}>
        <div className={styles.monthYearLegendContent}>
          <div className={styles.legendContainer}>
            <div className={styles.header}>
              Crescimento no {selectedFilter === "year" ? "Ano" : "Mês"}
            </div>

            {legends.map((legend) => (
              <TrendElement key={legend.name} data={legend} />
            ))}
          </div>

          <div className={styles.chartContainer}>
            <div className={styles.filters}>
              <div />
              <div className="d-flex align-center">
                <If cond={selectedFilter === "year"}>
                  <ComboFilters
                    options={getLastSixYears(startOn2025)}
                    onSelect={(option) => setSelectedYear(option)}
                    selectedOption={selectedYear}
                  />
                </If>
              </div>
            </div>
            <div className={styles.monthYearChart}>
              <ReactECharts
                ref={echartsRef}
                option={options}
                style={{ width: "100%", height: "100%" }}
                notMerge={true}
                lazyUpdate={true}
                opts={{ renderer: "canvas" }}
                onEvents={{
                  legendselectchanged: handleLegendSelectChanged,
                  // mouseover: handleClick,
                }}
              />
            </div>
          </div>
        </div>
      </If>
    </div>
  );
};
